import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import type { ModuleEntry } from '@/config/moduleEntries';
import { AuthButton } from '@/core/auth/components/AuthButton';
import { SsoAuthButtons } from '@/core/auth/components/SsoAuthButtons';
import { resolveSsoErrorMessage } from '@/utils/ssoErrorMessage';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { STORAGE_KEYS } from '@/constants/storage';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { clearAuth, login, verifyMfaLogin } from '@/store/authSlice';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { clearPlatformOnboardingRequired } from '@/modules/onboarding/onboardingState';

const schema = z.object({
  email: z.string().email('Enter a valid work email'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type FormData = z.infer<typeof schema>;

export interface LoginFormProps {
  moduleEntry?: ModuleEntry;
  onLoginSuccess: (result: {
    masterAdmin: boolean;
    permissions?: string[];
    enabledModules?: string[];
    entryModule?: string | null;
    roleCode?: string | null;
  }) => void;
  showRegisterLink?: boolean;
  variant?: 'organization' | 'master';
}

export function LoginForm({
  moduleEntry,
  onLoginSuccess,
  showRegisterLink = true,
  variant = 'organization',
}: LoginFormProps) {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((s) => s.auth.loading);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const ssoError = searchParams.get('ssoError');
  const ssoDetail = searchParams.get('ssoDetail');
  const entryModule = moduleEntry?.erpModule ?? moduleEntry?.slug;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema), defaultValues: { rememberMe: true } });

  useEffect(() => {
    if (!ssoError) return;
    notify.error(resolveSsoErrorMessage(ssoError, ssoDetail, 'login'));
    searchParams.delete('ssoError');
    searchParams.delete('ssoDetail');
    setSearchParams(searchParams, { replace: true });
  }, [ssoError, ssoDetail, searchParams, setSearchParams]);

  useEffect(() => {
    if (entryModule) {
      sessionStorage.setItem(STORAGE_KEYS.PENDING_ENTRY_MODULE, entryModule);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
    }
  }, [entryModule]);

  const onSubmit = async (data: FormData) => {
    try {
      const result = await dispatch(
        login({
          email: data.email,
          password: data.password,
          rememberMe: data.rememberMe,
          entryModule,
          masterLogin: variant === 'master',
        }),
      ).unwrap();
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        notify.info('Enter the code from your authenticator app', { icon: '🔐' });
        return;
      }
      if (variant === 'master' && !result.masterAdmin) {
        dispatch(clearAuth());
        notify.error('This account is not a platform master administrator');
        return;
      }
      if (variant === 'organization' && result.masterAdmin) {
        dispatch(clearAuth());
        notify.error('Platform operators must use the master console login URL.');
        return;
      }
      if (moduleEntry) {
        clearPlatformOnboardingRequired();
      }
      onLoginSuccess(result);
      notify.success('Welcome back');
    } catch (err) {
      showApiError(err, 'Invalid email or password');
    }
  };

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || totpCode.length < 6) return;
    try {
      const result = await dispatch(
        verifyMfaLogin({
          mfaToken,
          totpCode,
          entryModule,
          masterLogin: variant === 'master',
        }),
      ).unwrap();
      if (variant === 'master' && !result.masterAdmin) {
        dispatch(clearAuth());
        notify.error('This account is not a platform master administrator');
        return;
      }
      if (variant === 'organization' && result.masterAdmin) {
        dispatch(clearAuth());
        notify.error('Platform operators must use the master console login URL.');
        return;
      }
      if (moduleEntry) {
        clearPlatformOnboardingRequired();
      }
      onLoginSuccess(result);
      notify.success('Welcome back');
    } catch (err) {
      showApiError(err, 'Invalid authenticator code');
    }
  };

  if (mfaToken) {
    return (
      <AuthCard title="Two-factor authentication">
        <form onSubmit={onMfaSubmit} className="space-y-4">
          <AuthInput
            label="Authenticator code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <AuthButton type="submit" loading={loading} disabled={totpCode.length < 6}>
            Verify
          </AuthButton>
          <button
            type="button"
            className="w-full text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            onClick={() => {
              setMfaToken(null);
              setTotpCode('');
            }}
          >
            Back to login
          </button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <AuthInput label="Work email" type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
        <AuthInput
          label="Password"
          type="password"
          autoComplete="current-password"
          {...register('password')}
          error={errors.password?.message}
        />
        <div className="flex items-center justify-between gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <input type="checkbox" className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" {...register('rememberMe')} />
            Remember me
          </label>
          <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
            Forgot password?
          </Link>
        </div>
        <AuthButton type="submit" loading={loading}>
          Login
        </AuthButton>
        {variant === 'organization' ? (
          <SsoAuthButtons
            redirectAfter={moduleEntry?.homePath ?? '/dashboard/home'}
            entryModule={entryModule}
            googleLabel="Login with Google"
          />
        ) : (
          <SsoAuthButtons redirectAfter="/master" googleLabel="Login with Google" showMicrosoft={false} />
        )}
      </form>
      {showRegisterLink && variant === 'organization' ? (
        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          New organization?{' '}
          <Link
            to={moduleEntry?.registerPath ?? '/register'}
            className="font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Create account
          </Link>
        </p>
      ) : null}
    </AuthCard>
  );
}
